/**
 * SocialPublish — a "Connected Accounts N/7" bar on the finished-video screen that
 * expands to a Post button per CONNECTED, publishable account (TikTok / Facebook /
 * Instagram / Threads / LinkedIn / YouTube). Text/options per platform:
 *   • Video caption (with an AI composer) → Facebook / Instagram / Threads / LinkedIn.
 *   • Video title                         → YouTube (title-only platforms).
 *   • TikTok → ONE-TAP Direct Post using saved settings; a gear opens those settings
 *     (privacy, interaction, disclosure, caption). Publishes straight to the profile.
 * POST-ONLY: connecting happens on the Integrations page, never here.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, ChevronDown, Users2, ArrowRight, Sparkles, Pencil, Info } from 'lucide-react';
import { cn } from '@/v2/utils';
import { trackVideoShared } from '@/services/analyticsService';
import { BrandIcon, BRAND } from '@/v2/components/BrandIcons';
import { CaptionComposer } from '@/v2/components/CaptionComposer';
import { useIntegrationsAllowed, publishableProvidersFor } from '@/v2/integrationsAccess';
import type { V2VideoPlan } from '@/api/v2Service';
import {
  fetchSocialAccounts,
  fetchSocialPostStatus,
  postVideoToSocial,
  usesCaption,
  usesTitle,
  ALL_PROVIDERS,
  type SocialProvider,
  type SocialAccount,
  type TikTokPostInfo,
} from '@/v2/social';
import { TikTokSettingsDialog } from '@/v2/components/TikTokSettingsDialog';

const SUCCESS = ['SEND_TO_USER_INBOX', 'PUBLISH_COMPLETE'];
// 'posting' = upload request in flight; 'processing' = upload accepted, publishing
// continues server-side (safe to leave the screen); 'done'/'error' = settled.
type Phase = 'idle' | 'posting' | 'processing' | 'done' | 'error';

export function SocialPublish({ videoUrl, title, plan, orderId, videoProvider, fallbackFrom, approvedAt }: {
  videoUrl: string;
  title?: string;
  plan?: V2VideoPlan | null;
  /** Analytics context from the player — the share event carries the order and its provider. */
  orderId?: string;
  videoProvider?: string | null;
  fallbackFrom?: string | null;
  approvedAt?: string | null;
}) {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const allowed = useIntegrationsAllowed();
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [ytTitle, setYtTitle] = useState(title || '');   // YouTube (title-only) platforms
  const [caption, setCaption] = useState('');            // FB / IG / Threads caption
  const [captionOpen, setCaptionOpen] = useState(false); // AI caption composer window
  const [phase, setPhase] = useState<Record<string, Phase>>({});
  const [errText, setErrText] = useState<Record<string, string>>({});
  const [ttPostOpen, setTtPostOpen] = useState(false);   // TikTok compliant per-post dialog

  useEffect(() => {
    fetchSocialAccounts().then(setAccounts).catch(() => { /* show 0 */ }).finally(() => setLoaded(true));
  }, []);

  // Tiered: allowlist → all providers; public → PUBLIC_PROVIDERS (LinkedIn) only.
  // The counter's denominator is ALL platforms (incl. coming soon) — "N/13".
  const publishableSet = new Set<SocialProvider>(publishableProvidersFor(allowed));
  const connectedCount = accounts.length;
  const total = ALL_PROVIDERS.length; // 13
  const publishable = accounts.filter((a) => publishableSet.has(a.provider as SocialProvider));
  // Only show a field if a connected, publishable platform actually consumes it.
  const showCaption = publishable.some((a) => usesCaption(a.provider as SocialProvider));
  const showTitle = publishable.some((a) => usesTitle(a.provider as SocialProvider));
  // Each platform gets the text it uses: Google Drive → the video title (used as the
  // Drive file name); title-platforms → the title; everyone else → the caption.
  const textFor = (p: SocialProvider): string =>
    p === 'googledrive' ? (title || caption || '') : (usesTitle(p) ? ytTitle : caption);

  const pollUntilSettled = async (provider: SocialProvider, publishId: string) => {
    for (let i = 0; i < 96; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      try {
        const res = await fetchSocialPostStatus(provider, publishId);
        // Fires once, only when the post actually lands. `share_platform` is the social
        // account (`platform` is already a base Amplitude prop); `provider` the video's.
        // Amplitude gets operation_type 'video' like every other share; GA keeps the platform.
        if (SUCCESS.includes(res.status)) {
          trackVideoShared(provider, { operation_type: 'video', share_method: 'social_publish', share_platform: provider, order_id: orderId, provider: videoProvider || undefined, fallback_from: fallbackFrom || undefined, approved_at: approvedAt || undefined });
          setPhase((p) => ({ ...p, [provider]: 'done' })); return;
        }
        if (res.status === 'FAILED') { setErrText((e) => ({ ...e, [provider]: res.fail_reason || t('socialPublish.failed') })); setPhase((p) => ({ ...p, [provider]: 'error' })); return; }
      } catch { /* keep polling */ }
    }
    setPhase((p) => ({ ...p, [provider]: 'done' }));
  };

  const publish = async (provider: SocialProvider, postInfo?: TikTokPostInfo) => {
    setPhase((p) => ({ ...p, [provider]: 'posting' }));
    setErrText((e) => ({ ...e, [provider]: '' }));
    try {
      const publishId = await postVideoToSocial(provider, videoUrl, textFor(provider), postInfo);
      // Upload accepted — publishing now continues server-side, so the user can leave.
      setPhase((p) => ({ ...p, [provider]: 'processing' }));
      await pollUntilSettled(provider, publishId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      setErrText((x) => ({ ...x, [provider]: msg === 'reconnect_required' ? t('socialPublish.reconnect') : (msg || t('socialPublish.failed')) }));
      setPhase((p) => ({ ...p, [provider]: 'error' }));
    }
  };

  if (!loaded) return null;

  return (
    <div className="rounded-2xl border border-glass-border bg-surface-light/40 overflow-hidden">
      {/* Header bar — "Connected Accounts N/6" */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-surface-light/60 transition-colors"
        aria-expanded={open}
      >
        <Users2 className="w-4 h-4 text-primary shrink-0" />
        <span className="text-sm font-semibold text-text-primary">{t('socialPublish.connectedAccounts')}</span>
        <span className="px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[11px] font-bold tabular-nums">{connectedCount}/{total}</span>
        <ChevronDown className={cn('w-4 h-4 text-text-tertiary ml-auto transition-transform', open && 'rotate-180')} />
      </button>

      <div className={cn('grid transition-[grid-template-rows] duration-300 ease-out', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
        <div className="overflow-hidden min-h-0">
          <div className="px-4 pb-4 pt-1 space-y-2.5">
            {publishable.length === 0 ? (
              <button
                onClick={() => navigate('/integrations')}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border border-glass-border bg-surface hover:border-primary/30 transition-colors text-sm text-text-secondary"
              >
                {t('socialPublish.connectPrompt')} <ArrowRight className="w-4 h-4 ml-auto" />
              </button>
            ) : (
              <>
                {/* Title (YouTube) + caption (FB/IG/Threads/LinkedIn) — one composer
                    window edits both; click opens it. */}
                {(showCaption || showTitle) && (
                  <button
                    onClick={() => setCaptionOpen(true)}
                    className="w-full flex items-start gap-2 text-left bg-surface border border-glass-border rounded-xl px-3 py-2 hover:border-primary/40 transition-colors group"
                  >
                    <span className="flex-1 min-w-0">
                      {showTitle && ytTitle && (
                        <span className="block text-sm font-semibold text-text-primary truncate">{ytTitle}</span>
                      )}
                      <span className={cn('block text-sm whitespace-pre-wrap line-clamp-2', caption ? 'text-text-primary' : 'text-text-tertiary')}>
                        {caption || t('socialPublish.descriptionPlaceholder')}
                      </span>
                    </span>
                    {caption || (showTitle && ytTitle)
                      ? <Pencil className="w-3.5 h-3.5 text-text-tertiary shrink-0 mt-0.5" />
                      : <span className="flex items-center gap-1 text-[11px] font-semibold text-primary shrink-0 mt-0.5"><Sparkles className="w-3.5 h-3.5" /> {t('captionComposer.generate')}</span>}
                  </button>
                )}
                {publishable.map((a) => {
                  const provider = a.provider as SocialProvider;
                  const brand = BRAND[provider];
                  const ph = phase[provider] || 'idle';
                  const busyPh = ph === 'posting' || ph === 'processing';
                  const handle = a.username ? `@${a.username}` : a.display_name;
                  const icon = busyPh ? <Loader2 className="w-4 h-4 text-white animate-spin" />
                    : ph === 'done' ? <Check className="w-4 h-4 text-white" />
                    : <BrandIcon provider={provider} className="w-4 h-4 text-white" />;
                  const label = busyPh ? t('socialPublish.posting', { name: brand.label })
                    : ph === 'done' ? t('socialPublish.posted', { name: brand.label })
                    : ph === 'error' ? t('socialPublish.retry', { name: brand.label })
                    : t('socialPublish.postTo', { name: brand.label });
                  const sub = ph === 'error' && errText[provider] ? errText[provider] : handle;
                  const iconEl = <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: brand.color }}>{icon}</span>;
                  const textEl = (
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-text-primary truncate">{label}</span>
                      <span className="block text-[11px] text-text-tertiary truncate">{sub}</span>
                    </span>
                  );

                  // TikTok: opens the compliant per-post Direct Post dialog (the user actively
                  // selects privacy + interaction + disclosure per TikTok's Content Sharing
                  // Guidelines — no one-tap, no pre-selected privacy).
                  if (provider === 'tiktok') {
                    return (
                      <button
                        key={provider}
                        onClick={() => { if (ph === 'idle' || ph === 'error') setTtPostOpen(true); }}
                        disabled={busyPh || ph === 'done'}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors text-left',
                          ph === 'done' ? 'border-primary/30 bg-primary/10' : 'border-glass-border bg-surface hover:border-primary/30',
                        )}
                      >
                        {iconEl}{textEl}
                      </button>
                    );
                  }

                  return (
                    <button
                      key={provider}
                      onClick={() => { if (ph === 'idle' || ph === 'error') void publish(provider); }}
                      disabled={busyPh || ph === 'done'}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors text-left',
                        ph === 'done' ? 'border-primary/30 bg-primary/10' : 'border-glass-border bg-surface hover:border-primary/30',
                      )}
                    >
                      {iconEl}{textEl}
                    </button>
                  );
                })}
                {/* Reassurance once an upload is accepted: publishing continues server-side. */}
                {Object.values(phase).some((p) => p === 'processing') && (
                  <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-primary/10 border border-primary/20 text-[12px] text-text-secondary">
                    <Info className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                    {t('socialPublish.canLeave')}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {captionOpen && (
        <CaptionComposer
          value={caption}
          onChange={setCaption}
          plan={plan}
          videoTitle={title}
          title={ytTitle}
          onTitleChange={setYtTitle}
          showTitle={showTitle}
          onClose={() => setCaptionOpen(false)}
        />
      )}

      {ttPostOpen && (
        <TikTokSettingsDialog
          initialCaption={caption || title || ''}
          onClose={() => setTtPostOpen(false)}
          onPost={(info) => { setTtPostOpen(false); void publish('tiktok', info); }}
        />
      )}
    </div>
  );
}
